import { useEffect, useRef, useState } from 'react';
import type {
  SearchAggregate,
  SearchHistoryCounts,
  SearchPeriod,
  SearchProvider,
  SearchSourceView,
} from '../../../api/src/search-metrics/search-metrics.types';
import { Box, Stack, Typography } from '../components/mui-system';
import { ButtonBase, Chip, CircularProgress, Tooltip } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { SourceIcon, sourceNames, sourceColors } from '../components/SourceIcon';
export { SourceIcon, sourceNames, sourceColors } from '../components/SourceIcon';

export const metricNames = {
  clicks: 'Клики',
  impressions: 'Показы',
  ctr: 'CTR',
  position: 'Средняя позиция',
};
export type ChartMetric = keyof typeof metricNames;
export const chartModeNames = { traffic: 'Клики и показы', ...metricNames };
export type ChartMode = keyof typeof chartModeNames;
const metricLineStyles: Record<ChartMetric, 'solid' | 'dashed'> = {
  clicks: 'solid',
  impressions: 'dashed',
  ctr: 'solid',
  position: 'solid',
};
const compactNumber = new Intl.NumberFormat('en', { notation: 'compact' });
export function number(value: number | null, digits = 0): string {
  return value === null ? '—' : value.toLocaleString('ru-RU', { maximumFractionDigits: digits });
}
export function metric(value: number | null, kind: ChartMetric): string {
  return `${number(value, kind === 'ctr' ? 2 : kind === 'position' ? 1 : 0)}${kind === 'ctr' && value !== null ? '%' : ''}`;
}
export function compactMetric(value: number | null, kind: ChartMetric): string {
  return (kind === 'clicks' || kind === 'impressions') && value !== null
    ? compactNumber.format(value)
    : metric(value, kind);
}
export function readableDate(value: string | null): string {
  if (!value) return '—';
  const date = value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
  return date.toLocaleDateString('ru-RU');
}
export function MetricsValues({
  value,
  change,
  summary = false,
  showCoverage = true,
}: {
  value: SearchAggregate;
  change: number | null;
  summary?: boolean;
  showCoverage?: boolean;
}) {
  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(4, minmax(0, 1fr))"
        gap={1}
        sx={{
          '@container (max-width: 280px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {(Object.keys(metricNames) as ChartMetric[]).map((kind) => (
          <Box key={kind}>
            <Typography color="text.secondary" fontSize={11}>
              {kind === 'position' ? 'Ср. позиция' : metricNames[kind]}
            </Typography>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: 'clamp(15px, 5cqw, 20px)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                '@container (max-width: 280px)': { fontSize: 20 },
              }}
            >
              {compactMetric(value[kind], kind)}
            </Typography>
          </Box>
        ))}
      </Box>
      {(change !== null || showCoverage) && (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mt={1}>
          {change !== null && (
            <Chip
              size="small"
              color={change >= 0 ? 'success' : 'error'}
              variant="outlined"
              label={`${change > 0 ? '+' : ''}${number(change, 1)}% кликов`}
            />
          )}
          {showCoverage && (
            <Typography color="text.secondary" fontSize={11}>
              {value.days === 0
                ? 'Нет данных за период'
                : value.days < value.expectedDays
                  ? `Частичные данные · ${value.days} из ${value.expectedDays} дней${summary ? ' по сайтам' : ''}`
                  : 'Полный период'}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

export function SourceCoverage({ source }: { source: SearchSourceView }) {
  const [open, setOpen] = useState(false);
  const { days, expectedDays } = source.current;
  // Coverage describes saved daily totals, not the success of a refresh or API detail completeness.
  const percent = expectedDays > 0 ? Math.min(100, Math.floor((days * 100) / expectedDays)) : null;
  const warning = Boolean(
    source.warnings?.length ||
    source.lastRun?.error ||
    ['error', 'partial'].includes(source.lastRun?.status ?? '') ||
    !source.connectionIds.length,
  );
  const label = `${sourceNames[source.property.provider]} · Данные за период: ${percent === null ? '—' : `${percent}%`}`;
  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      describeChild
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            boxShadow: 4,
            maxWidth: 360,
            p: 1.5,
          },
        },
        arrow: { sx: { color: 'background.paper' } },
      }}
      title={
        <Stack gap={1}>
          <Typography fontSize={12} fontWeight={700}>
            {label}
          </Typography>
          <Typography fontSize={12}>
            Данные за выбранный период: {days} из {expectedDays} дней с кликами и показами.
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            Кольцо показывает полноту сохранённых данных, а не ход фоновой загрузки.
          </Typography>
          <SourceStatus source={source} compact />
        </Stack>
      }
    >
      <ButtonBase
        aria-label={label}
        onClick={() => setOpen(true)}
        sx={{
          position: 'relative',
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '50%',
          color: warning ? 'warning.main' : sourceColors[source.property.provider],
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <CircularProgress
            aria-hidden="true"
            variant="determinate"
            value={100}
            size={32}
            thickness={3.5}
            sx={{
              color: 'action.disabledBackground',
              position: 'absolute',
              inset: 0,
              ...(percent === null ? { '& circle': { strokeDasharray: '2 3' } } : {}),
            }}
          />
          {percent !== null && (
            <CircularProgress
              aria-label={label}
              variant="determinate"
              value={percent}
              size={32}
              thickness={3.5}
              color="inherit"
              sx={{ position: 'absolute', inset: 0 }}
            />
          )}
        </Box>
        <SourceIcon provider={source.property.provider} />
        {(warning || percent === null) && (
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              color: warning ? 'warning.main' : 'text.secondary',
              fontSize: 10,
              fontWeight: 800,
              lineHeight: '12px',
              boxShadow: '0 0 0 1px currentColor',
            }}
          >
            {warning ? '!' : '?'}
          </Box>
        )}
      </ButtonBase>
    </Tooltip>
  );
}

export function SourceStatus({
  source,
  compact = false,
}: {
  source: SearchSourceView;
  compact?: boolean;
}) {
  const run = source.lastRun;
  const active = run && ['queued', 'running'].includes(run.status);
  return (
    <Stack gap={0.4}>
      <Typography
        color={run?.error || !source.connectionIds.length ? 'warning.main' : 'text.secondary'}
        fontSize={11}
      >
        {sourceNames[source.property.provider]} ·{' '}
        {!source.connectionIds.length
          ? 'Аккаунт не подключён · показана сохранённая история'
          : active
            ? 'Загрузка данных…'
            : run?.status === 'error'
              ? 'Ошибка загрузки'
              : run?.status === 'partial'
                ? 'Загружена часть данных'
                : source.lastSuccessAt
                  ? `Обновлено ${readableDate(source.lastSuccessAt)} · данные до ${readableDate(source.current.lastDate)}`
                  : 'Данные ещё не загружены'}
      </Typography>
      {source.history &&
        (compact ? (
          <Typography fontSize={11} color="text.secondary">
            История: {readableDate(source.history.dateFrom)} — {readableDate(source.history.dateTo)}
          </Typography>
        ) : (
          <Box component="details">
            <Typography
              component="summary"
              fontSize={11}
              color="text.secondary"
              sx={{ cursor: 'pointer' }}
            >
              История: {readableDate(source.history.dateFrom)} —{' '}
              {readableDate(source.history.dateTo)}
            </Typography>
            {source.history.dimensions.map((value) => (
              <Box key={value.dimension}>
                <Typography fontSize={11} color="text.secondary">
                  {value.dimension === 'total'
                    ? 'Итоги'
                    : value.dimension === 'query'
                      ? 'Запросы'
                      : 'Страницы'}
                </Typography>
                <HistoryCounts value={value} />
              </Box>
            ))}
          </Box>
        ))}
      {source.warnings?.map((warning) => (
        <Typography key={warning} color="warning.main" fontSize={11}>
          {warning}
        </Typography>
      ))}
      {run?.error && (
        <Typography color="warning.main" fontSize={11}>
          {run.error}
        </Typography>
      )}
    </Stack>
  );
}

export function HistoryCounts({ value }: { value: SearchHistoryCounts }) {
  const uncertain = value.partial + value.limited + value.error;
  return (
    <Typography
      fontSize={11}
      color={uncertain || value.retained ? 'warning.main' : 'text.secondary'}
    >
      Проверено {value.checkedDays} из {value.expectedDays} дней · без замечаний {value.loaded}
      {value.pending > 0 && ` · ожидают проверки ${value.pending}`}
      {value.empty > 0 && ` · пустой ответ ${value.empty}`}
      {uncertain > 0 && ` · требуют проверки ${uncertain}`}
      {value.unavailable > 0 && ` · недоступны в API ${value.unavailable}`}
      {value.retained > 0 && ` · оставлены прежние данные ${value.retained}`}
    </Typography>
  );
}

/** Missing days split lines; unknown values must never look like zero traffic. */
export function MetricsChart({
  sources,
  period,
  kind,
  large = false,
  compact = false,
  edgeToEdge = false,
}: {
  sources: SearchSourceView[];
  period: SearchPeriod;
  kind: ChartMode;
  large?: boolean;
  compact?: boolean;
  edgeToEdge?: boolean;
}) {
  const start = Date.parse(period.dateFrom);
  const end = Date.parse(period.dateTo);
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    if (!host.current || !window.ResizeObserver) return;
    const observer = new window.ResizeObserver(([entry]) =>
      setWidth(Math.max(240, entry!.contentRect.width)),
    );
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  const height = large ? 230 : compact ? 90 : 155;
  const ticks = compact ? [0, 1] : [0, 0.5, 1];
  const padding = edgeToEdge ? 6 : 38;
  const combined = kind === 'traffic';
  const metrics: ChartMetric[] = combined ? ['clicks', 'impressions'] : [kind];
  const right = width - (edgeToEdge ? 6 : combined ? padding : 12);
  const axes = metrics.map((key) => ({
    key,
    max: sources.reduce(
      (max, source) => source.series.reduce((value, p) => Math.max(value, p[key] ?? 0), max),
      1,
    ),
  }));
  const lines = sources.flatMap((source) =>
    axes
      .filter(({ key }) => source.series.some((point) => point[key] !== null))
      .map((axis) => ({
        ...axis,
        source,
        color: sourceColors[source.property.provider],
        dashed: metricLineStyles[axis.key] === 'dashed',
      })),
  );
  const x = (date: string) =>
    padding + ((Date.parse(date) - start) / Math.max(end - start, 86400000)) * (right - padding);
  const bottom = height - (edgeToEdge ? 17 : 22);
  const top = edgeToEdge ? 12 : 20;
  const y = (fraction: number) => bottom - fraction * (bottom - top);
  if (!lines.length)
    return (
      <Box
        ref={host}
        minHeight={height}
        display="grid"
        sx={{
          gridTemplateColumns: 'minmax(0, 1fr)',
          placeItems: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
          p: 1,
        }}
      >
        <Typography
          color="text.secondary"
          fontSize={13}
          textAlign="center"
          sx={{ minWidth: 0, maxWidth: '100%' }}
        >
          График появится после загрузки данных
        </Typography>
      </Box>
    );
  return (
    <Box
      ref={host}
      sx={(theme: Theme) => ({
        color: 'text.secondary',
        ...(edgeToEdge
          ? {
              '& text': {
                paintOrder: 'stroke',
                stroke: theme.palette.background.paper,
                strokeWidth: 3,
                strokeLinejoin: 'round',
              },
            }
          : {}),
      })}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`${chartModeNames[kind]} за выбранный период`}
        style={{ display: 'block' }}
      >
        {ticks.map((t) => (
          <line
            key={t}
            x1={padding}
            x2={right}
            y1={y(t)}
            y2={y(t)}
            stroke="currentColor"
            strokeOpacity=".16"
            strokeDasharray="4 5"
          />
        ))}
        {lines.map(({ source, key, max, color, dashed }) => {
          let lastDate: string | null = null;
          const points = source.series;
          const path = points
            .map((point) => {
              const value = point[key];
              if (value === null) {
                lastDate = null;
                return '';
              }
              const command =
                lastDate && Date.parse(point.date) - Date.parse(lastDate) === 86400000 ? 'L' : 'M';
              lastDate = point.date;
              return `${command}${x(point.date).toFixed(1)},${y(value / max).toFixed(1)}`;
            })
            .join(' ');
          return (
            <g
              key={`${source.property.id}:${key}`}
              aria-label={`${sourceNames[source.property.provider]} · ${metricNames[key]}`}
            >
              <path
                d={path}
                fill="none"
                stroke={color}
                strokeDasharray={dashed ? '6 4' : undefined}
                strokeWidth={compact ? 2 : 2.5}
                strokeLinejoin="round"
              />
              {points
                .filter((p) => p[key] !== null)
                .map((point) => (
                  <circle
                    key={point.date}
                    cx={x(point.date)}
                    cy={y(point[key]! / max)}
                    r={points.length > 100 ? 1 : compact ? 1.6 : 2.4}
                    fill={color}
                  >
                    <title>
                      {sourceNames[source.property.provider]} · {readableDate(point.date)} ·{' '}
                      {metricNames[key]}: {metric(point[key], key)}
                    </title>
                  </circle>
                ))}
            </g>
          );
        })}
        {ticks.flatMap((t) =>
          axes.map((axis, index) => (
            <text
              key={`${axis.key}:${t}`}
              x={index === 0 ? padding + (edgeToEdge ? 3 : -5) : right + (edgeToEdge ? -3 : 5)}
              y={y(t) + (edgeToEdge ? (t === 0 ? -4 : 12) : 4)}
              textAnchor={
                index === 0 ? (edgeToEdge ? 'start' : 'end') : edgeToEdge ? 'end' : 'start'
              }
              fill="currentColor"
              fontSize="10"
            >
              {axis.key === 'clicks' || axis.key === 'impressions'
                ? compactNumber.format(t * axis.max)
                : number(t * axis.max, 1)}
            </text>
          )),
        )}
        <text x={padding} y={height - 3} fill="currentColor" fontSize="10">
          {readableDate(period.dateFrom)}
        </text>
        <text x={right} y={height - 3} fill="currentColor" textAnchor="end" fontSize="10">
          {readableDate(period.dateTo)}
        </text>
      </svg>
    </Box>
  );
}

export function MetricsChartLegend({
  providers,
  kind,
}: {
  providers: SearchProvider[];
  kind: ChartMode;
}) {
  const visibleProviders = (Object.keys(sourceNames) as SearchProvider[]).filter((provider) =>
    providers.includes(provider),
  );
  const metrics: ChartMetric[] = kind === 'traffic' ? ['clicks', 'impressions'] : [kind];
  return (
    <Box role="group" aria-label="Обозначения графиков">
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Typography fontSize={12} color="text.secondary">
          На графиках:
        </Typography>
        {visibleProviders.map((provider) => (
          <Stack key={provider} direction="row" gap={0.75} alignItems="center">
            <Box
              aria-hidden="true"
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sourceColors[provider] }}
            />
            <Typography fontSize={12}>{sourceNames[provider]}</Typography>
          </Stack>
        ))}
        {metrics.map((key) => (
          <Stack key={key} direction="row" gap={0.75} alignItems="center">
            <Box
              aria-hidden="true"
              sx={{
                width: 22,
                borderTop: `2px ${metricLineStyles[key]}`,
                borderColor: 'text.secondary',
              }}
            />
            <Typography fontSize={12}>
              {metricNames[key]}
              {kind === 'traffic' && (key === 'clicks' ? ' · слева' : ' · справа')}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {kind === 'traffic' && (
        <Typography fontSize={11} color="text.secondary" mt={1}>
          Шкалы кликов и показов независимы: одинаковая высота линий не означает одинаковые
          значения.
        </Typography>
      )}
    </Box>
  );
}
